import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { TranslatePipe } from 'eshava-shared';

import { Icon } from '@portal/shared/ui/icon/icon';
import { SecretService } from './secret.service';

/**
 * Shows a freshly created secret, once.
 *
 * A native `<dialog>` opened with `showModal`, like the confirmation -- but here blocking **is** the
 * function rather than a side effect: the value cannot be read again, so closing the dialog is the
 * moment it is gone. That is why the button says so instead of saying "OK", and why there is no
 * second way out that could be hit by accident.
 *
 * Escape still closes it. Trapping someone in a dialog to protect them from themselves is worse than
 * the mistake it prevents -- and the copy button is right there, focused, from the moment it opens.
 */
@Component({
  selector: 'portal-secret-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslatePipe],
  template: `
    <dialog #dialog class="secret" (close)="onClosed()">
      @if (secret(); as pending) {
        <h2 class="secret__title">{{ pending.titleKey | t }}</h2>
        <p class="secret__subject">{{ pending.name }}</p>

        <p class="secret__warning" role="alert">
          <portal-icon name="key-solid" size="sm" />
          <span>{{ 'secret.onlyNow' | t }}</span>
        </p>

        <div class="secret__value">
          <code class="secret__code">{{ pending.secret }}</code>
          <button #copy type="button" class="secret__copy" (click)="copySecret(pending.secret)">
            <portal-icon name="copy-solid" size="sm" />
            {{ (copied() ? 'common.copied' : 'common.copy') | t }}
          </button>
        </div>

        <div class="secret__actions">
          <button type="button" class="secret__button" (click)="close()">{{ 'secret.done' | t }}</button>
        </div>
      }
    </dialog>
  `,
  styles: `
    .secret {
      max-width: min(34rem, calc(100vw - 2rem));
      padding: var(--space-4);
      color: var(--text);
      background-color: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-md, var(--radius-sm));
      box-shadow: var(--shadow-card);
    }

    .secret::backdrop {
      background-color: rgba(0, 0, 0, 0.45);
    }

    .secret__title {
      margin: 0;
      font-size: var(--text-base);
    }

    .secret__subject {
      margin: 2px 0 var(--space-3);
      font-size: var(--text-xs);
      color: var(--text-subtle);
    }

    .secret__warning {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      max-width: var(--content-max-text);
      padding: var(--space-2) var(--space-3);
      margin: 0 0 var(--space-3);
      color: var(--warning);
      background-color: var(--warning-soft);
      border: 1px solid var(--warning-border);
      border-radius: var(--radius-sm);
    }

    .secret__value {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-2);
      align-items: center;
    }

    /* Breaks anywhere: a secret is a run of characters with no word boundaries, and letting it
       overflow would put half of it outside the dialog. */
    .secret__code {
      flex: 1;
      min-width: 0;
      padding: var(--space-2) var(--space-3);
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: var(--text-sm);
      word-break: break-all;
      background-color: var(--surface-sunken);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
    }

    .secret__copy,
    .secret__button {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
      min-height: 2.25rem;
      padding: 0 var(--space-3);
      font: inherit;
      color: var(--text-muted);
      background: none;
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      cursor: pointer;
    }

    .secret__copy:hover,
    .secret__button:hover {
      color: var(--text);
      background-color: var(--surface-raised);
    }

    .secret__actions {
      display: flex;
      justify-content: flex-end;
      margin-top: var(--space-4);
    }

    @media (pointer: coarse) {
      .secret__copy,
      .secret__button {
        min-height: 2.75rem;
      }
    }
  `
})
export class SecretDialog {
  private readonly secrets = inject(SecretService);

  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  private readonly copyButton = viewChild<ElementRef<HTMLButtonElement>>('copy');

  protected readonly secret = computed(() => this.secrets.pending());
  protected readonly copied = signal(false);

  constructor() {
    effect(() => {
      const element = this.dialog().nativeElement;

      if (this.secret() !== null) {
        if (!element.open) {
          element.showModal();
          this.copied.set(false);
          /* The copy button is what someone came for, so it is where the focus starts -- not on the
             button that throws the value away. */
          queueMicrotask(() => this.copyButton()?.nativeElement.focus());
        }
      } else if (element.open) {
        element.close();
      }
    });
  }

  protected async copySecret(secret: string): Promise<void> {
    await navigator.clipboard.writeText(secret);
    this.copied.set(true);
  }

  protected close(): void {
    this.dialog().nativeElement.close();
  }

  /** However it closed -- the button, Escape, anything -- the value is dropped. */
  protected onClosed(): void {
    this.secrets.dismiss();
  }
}
